// import library
import { createContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CryptoJS from 'crypto-js';
import axios from "axios";
// import files
import { env } from "../env";
import i18n from '../i18n/config';

export const AuthContext = createContext({
    userInfo: null,
    isAuthenticated: false,
    token: null,
    loginPageLogo: null,
    loginPageBackgroundImage: null,
    companyWebsite: null,
    isCompanyLoading: true,
    login: () => {},
    logout: () => {},
});

const AuthContextProvider = ({ children }) => {
    // variables
    const navigate = useNavigate();
    const location = useLocation();
    const [userInfo, setUserInfo] = useState(null);
    const [isAuthenticated, setisAuthenticated] = useState(false);
    const [loginPageLogo, setLoginPageLogo] = useState("");
    const [loginPageBackgroundImage, setLoginPageBackgroundImage] = useState();
    const [companyWebsite, setCompanyWebsite] = useState("");
    const [isCompanyLoading, setIsCompanyLoading] = useState(!localStorage.getItem('userInfo'));
    const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;
    const baseUrl = env.REACT_APP_API_URL;
    const encrypt = localStorage.getItem('userInfo');
    const token = localStorage.getItem('token');

    // functions
    useEffect(() => {
        const fetchData = async () => {
            if (encrypt) {
                const stored = decryptData(encrypt);
                if (stored?.companyInfo) {
                    setLoginPageLogo(stored.companyInfo.adminLoginPageLogo);
                    setLoginPageBackgroundImage(stored.companyInfo.adminLoginPageBackgroundImage);
                    setCompanyWebsite(stored.companyInfo.website);
                    setIsCompanyLoading(false);
                    return;
                }
            }
            await axios.get(baseUrl+`/api/Company/GetCompanyInfoDetails?companyid=${env.REACT_APP_COMPANY_ID}`, { headers: {
                "Accept" : "Application/json",
                "Content-Type" : "Application/json"
            }}).then((response) => {
                if(response.data.statusCode === 200) {
                    setLoginPageLogo(response.data.data.adminLoginPageLogo);
                    setLoginPageBackgroundImage(response.data.data.adminLoginPageBackgroundImage);
                    setCompanyWebsite(response.data.data.website);
                }
            }).catch((error) => {
                console.log(error);
            }).finally(() => {
                setIsCompanyLoading(false);
            });
        };
        fetchData();
        
        // token check
        if(!token) {
            removeItem();
            return;
        }
        // data check
        if(!encrypt) {
            removeItem();
            return;
        } else {
            const decrypt = decryptData(encrypt);

            // check for expiry date
            const currentDate = Math.floor(Date.now() /1000);
            const expiryDate = decrypt.expiryDate;
            if(expiryDate) {
                if (expiryDate < currentDate) {
                    removeItem();
                    localStorage.setItem('remember', 0);
                    return;
                }
            } else {
                removeItem();
                localStorage.setItem('remember', 0);
                return;
            }
            // sessions created before this change still carry the plaintext password: drop it
            if(decrypt.password !== undefined) {
                delete decrypt.password;
                localStorage.setItem('userInfo', encryptData(decrypt));
            }
            setUserInfo(decrypt);
            setisAuthenticated(true);
            navigate(location.pathname);

            // Refresh clientToken on page reload if missing
            if (!localStorage.getItem('clientToken')) {
                axios.post(`${env.REACT_APP_RPC_URL}/rpc/login`, {
                    username: env.REACT_APP_RPC_USERNAME,
                    password: env.REACT_APP_RPC_PASSWORD,
                }).then((res) => {
                    if (res.data) localStorage.setItem('clientToken', res.data);
                }).catch((error) => {
                    console.log(error);
                });
            }
        }
    }, [isAuthenticated]);

    const removeItem = () => {
        if(location.pathname === '/' || location.pathname === '/forgot-password' || location.pathname === '/server-setting' || location.pathname === '/reset-password') {
            navigate(location.pathname);
        } else {
            navigate('/');
        }
        localStorage.removeItem("token");
        localStorage.removeItem("userInfo");
        localStorage.removeItem("loginUserId");
        localStorage.removeItem("clientToken");
        localStorage.removeItem("defaultLanguage");
        // locale retained on logout so language preference persists on login page
        localStorage.removeItem("isAdmin");
        localStorage.removeItem('co_tab');
        localStorage.removeItem('co_coupon');
        localStorage.removeItem('co_comment');
        localStorage.removeItem('co_method');
        localStorage.removeItem('co_id');
        localStorage.removeItem('co_quantity');
        localStorage.removeItem('co_qty_message');
        localStorage.removeItem('co_items');
        localStorage.removeItem('co_calculate');
        localStorage.removeItem('co_code');
        localStorage.removeItem('co_calculate_json');
        localStorage.removeItem('co_calculate_button');
        localStorage.removeItem('co_calculate_filter');
        localStorage.removeItem('profile_tab');
        localStorage.removeItem('co_shipping');
        localStorage.removeItem('co_customer');
        localStorage.removeItem('co_paymentDiscount');
        
        setUserInfo(null);
        setisAuthenticated(false);
    };
    const encryptData = (data) => {
        try {
            const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
            return encrypted;
        } catch (e) {
            removeItem();
        }
    };
    const decryptData = (data) => {
        try {
            const decrypted = CryptoJS.AES.decrypt(data, SECRET_KEY).toString(CryptoJS.enc.Utf8);
            return JSON.parse(decrypted);
        } catch (e) {
            removeItem();
            window.location.reload();
        }
    };
    const fetchTranslations = async (language, headers) => {
        const response = await axios.get(
          `${baseUrl}/api/SystemTranslation/GetSystemTranslationList?module=b2b&language=${language}`,
          { headers }
        );
      
        const translations = {};
        for (const item of response.data) {
          translations[item.key.toLowerCase()] = item.value || item.defaultvalue;
        }
      
        return translations;
    };
    const login = async (newToken, userData) => {
        localStorage.setItem("token", newToken);
        
        await axios.post(baseUrl + "/api/User/GetUserDetailsByUserID", { "UserName": userData.userName }, {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${newToken}`,
            },
        }).then( async (response) => {
            localStorage.setItem('loginUserId', response.data?.id);

            const supportedLangs = ['en', 'gr'];
            const existingLocale = localStorage.getItem('locale');
            const defaultLang = response.data.defaultLanguage;

            const newLanguage = (existingLocale && supportedLangs.includes(existingLocale))
                ? existingLocale
                : (supportedLangs.includes(defaultLang) ? defaultLang : supportedLangs[0]);

            i18n.changeLanguage(newLanguage);
            localStorage.setItem('locale', newLanguage);
            localStorage.setItem('defaultLanguage', 1);

            if (response.data.isAdministrator) {
                localStorage.setItem('isAdmin', 1);
            } else {
                localStorage.setItem('isAdmin', 0);
            }

            const headers = {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${newToken}`,
            }
      
            const [english, greek] = await Promise.all([
                fetchTranslations('en', headers),
                fetchTranslations('gr', headers),
            ]);

            i18n.addResourceBundle('en', 'translations', english, true, true);
            i18n.addResourceBundle('gr', 'translations', greek, true, true);
        }).catch((error) => {
            console.error("Error fetching user details:", error);
        });

        // Fetch and store client API token
        await axios.post(`${env.REACT_APP_RPC_URL}/rpc/login`, {
            username: env.REACT_APP_RPC_USERNAME,
            password: env.REACT_APP_RPC_PASSWORD,
        }).then((res) => {
            if (res.data) localStorage.setItem('clientToken', res.data);
        }).catch((error) => {
            console.log(error);
        });

        // Encrypt and store user data.
        const encryptedUserData = encryptData(userData);
        localStorage.setItem('userInfo', encryptedUserData);
      
        // Update local state.
        setUserInfo(decryptData(encryptedUserData));
        setisAuthenticated(true);
      
        // Navigate to the orders page.
        navigate("/orders");

        // Last step of the login flow: token is stored and the page is open.
        axios.get(baseUrl+"/api/Company/GetCompanyInfo", { headers: {
            "Content-Type" : "application/json",
            "Authorization" : `Bearer ${newToken}`
        }}).then((response) => {
            // apiKey comes from GetCompanyInfo; keep it in the stored session too
            const apiKey = response.data?.data?.apiKey ?? response.data?.apiKey;
            if (apiKey) {
                const updatedUserData = { ...userData, apiKey };
                localStorage.setItem('userInfo', encryptData(updatedUserData));
                setUserInfo(updatedUserData);
            }
        }).catch((error) => {
            console.log(error);
        });
    };
    const logout = () => {
        removeItem();
        navigate("/");
    };

    return (
        <AuthContext.Provider value={{userInfo, setUserInfo, isAuthenticated, loginPageLogo, loginPageBackgroundImage, companyWebsite, isCompanyLoading, setisAuthenticated, login, logout}}>
            {children}
        </AuthContext.Provider>
    );
};

export  {
    AuthContextProvider
};